import { useState, useCallback, useEffect } from 'react';
import { get, patch } from '../utils/api';

export interface UserRepresent {
  id: string;
  opt_out: boolean;
  institution: {
    id: string;
    name: string;
    type: string;
  };
}

interface UseInstitutionsOptions {
  userId: string | undefined;
  isStudent: boolean;
  isCorporate: boolean;
  enabled?: boolean;
}

export function useInstitutions({ userId, isStudent, isCorporate, enabled = true }: UseInstitutionsOptions) {
  const [institutions, setInstitutions] = useState<UserRepresent[]>([]);

  useEffect(() => {
    if (!enabled || !userId || (!isStudent && !isCorporate)) {
      setInstitutions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await get<{ institutions: UserRepresent[] }>('/api/user/represent');
        if (!cancelled && res.data?.institutions) {
          setInstitutions(res.data.institutions);
        }
      } catch {
        // Silently fail — toggle just won't show
      }
    })();
    return () => { cancelled = true; };
  }, [userId, isStudent, isCorporate, enabled]);

  const toggleRepresent = useCallback(async (inst: UserRepresent) => {
    const newOptOut = !inst.opt_out;
    // Optimistic update
    setInstitutions(prev => prev.map(i => i.id === inst.id ? { ...i, opt_out: newOptOut } : i));
    try {
      await patch(`/api/user/represent/${inst.id}`, { opt_out: newOptOut });
    } catch {
      // Revert on failure
      setInstitutions(prev => prev.map(i => i.id === inst.id ? { ...i, opt_out: inst.opt_out } : i));
    }
  }, []);

  return { institutions, toggleRepresent };
}
